/* @flow strict */

import * as React from 'react';
import * as axios from 'axios';
import _ from 'lodash';
// import i18n from 'i18next';
import t from 'client/src/utils/i18nCheckKey';
// import NProgress from 'nprogress';
import NProgressCustom from '/client/src/components/nProgress';

import available from '/client/src/utils/DOM_available';

/**
 * Stateless components for render request stage.
 */
import DefaultError, {
  type PropsType as ErrorPropsType
} from './Error';
import DefaultMessage, {
 type PropsType as MessagePropsType
} from './Message';
import DefaultPending, {
  type PropsType as PendingPropsType
} from './Pending';
import DefaultCanceled, {
  type PropsType as CanceledPropsType
} from './Canceled';

import {
  GET,
  type MethodType
} from '/types/api';

import { HOST } from '/client/src/api';
import Credentials from '/client/src/api/Credentials';

import './index.scss';

/**
 * Component for added extra behavior to load components data.
 * @extends Component
 * @param (required) {Function}  load - loader function,
 *                                      read handleLoad description for detail
 * @param (optional) {String}    loadMessage - load stage message
 * @param (optional) {String}    errorMessage - error stage message
 * @param (optional) {String}    cancelMessage - cancel stage message
 * @param (optional) {String}    emptyDataMessage - emptyData stage message
 * @param (optional) {Any}       watchParams - for refresh request, when passed and changed
 * @param (optional) {Function}  onLoaded - call whed request is loaded with params (error, result)
 * @param (optional) {Array of React components}  children
 */
type PropsType = {
  load: ({}) => void,
  loadMessage: string,
  errorMessage: string,
  cancelMessage: string,
  emptyDataMessage: string,
  watchParams?: any,
  onLoaded?: (?Error, ?*) => void,
  emptyDataCondition: (any) => boolan,
  children?: ?React.Node,
  disabled?: boolean,
  useNProgress: boolean,
  showChildrenAlways: boolean,

  Error: React.AbstractComponent<ErrorPropsType>,
  Message: React.AbstractComponent<MessagePropsType>,
  Pending: React.AbstractComponent<PendingPropsType>,
  Canceled: React.AbstractComponent<CanceledPropsType>,

  url: string,
  method: MethodType,
  options: {
    url?: string,
    method?: MethodType
  },
  customUrl?: boolean | null
};
type StateType = {
  pending: boolean,
  source: *,
  progress: number,
  result: any,
  canceled: boolean | null,
  error: Error | null
};

class Loader extends React.Component<PropsType, StateType> {
  static defaultProps = {
    load: axios.create({
      baseURL: HOST,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).request,
    useNProgress: false,
    showChildrenAlways: false,
    emptyDataCondition: (): boolean => false,
    loadMessage: t('Data is loading. Please wait.'),
    errorMessage: t('Error in loading time. Click to try reload data.'),
    cancelMessage: t('Canceled. Click to try load again.'),
    emptyDataMessage: t('Loaded, but data is empty.'),

    Error: DefaultError,
    Message: DefaultMessage,
    Pending: DefaultPending,
    Canceled: DefaultCanceled,

    url: available<{ href: string }>('location', { href: '/' }).href,
    method: GET,
    options: {}
  };

  state = {
    pending: true,
    source: null,
    progress: 0,
    result: null,
    canceled: null,
    error: null
  };

  /**
   * Triggered load from this.props.load
   */
  componentDidMount = (): void => this.handleLoad();

  /**
   * Behavior: when passed this.props.watchParams and this data are chenged trigger reload.
   */
  componentDidUpdate = (prevProps: PropsType) => {
    if (!_.isEqual(prevProps.watchParams, this.props.watchParams)) {
      if (!_.isNull(this.state.source) && this.state.source) {
        this.state.source.cancel();
      }

      this.handleLoad();
    }
  };

  /**
   * Canceling request if it wasn't finished
   */
  componentWillUnmount = () => {
    if (this.props.useNProgress) {
      NProgressCustom(false);
    }
    if (!_.isNull(this.state.source) && this.state.source) {
      this.state.source.cancel();
    }
  };

  /**
   * Extra behavior for this.props.load:
   *    - cancel request opportunity;
   *    - observe progress if it's possible;
   *    - show preloader;
   *    - show request stage;
   *    - refresh request opportunity.
   *
   * For correct use need passed function for call form loader when first param will get
   * "params" and passed these to load function in last param for passet that to httpRequest function,
   * for example Redux action:
   *
   *    const getUserManagementLists = params => dispatch => httpRequest({
   *      url: '/usermanagement',
   *      method: 'GET',
   *      cbCatch: () => {
   *        dispatch({ type: GET_USER_MANAGEMENT_LIST, payload: 'error' });
   *      }
   *    }, params)
   *      .then(({ data }) => {
   *        dispatch({ type: GET_USER_MANAGEMENT_LIST, payload: data.result });
   *        return data;
   *      });
   *
   * P.S.: This implementation used only for do not touch old interface for loading.
   */
  handleLoad = () => {
    if (this.props.disabled !== true) {
      this.setState(
        {
          pending: true,
          source: axios.CancelToken.source(),
          progress: 0,
          result: null,
          canceled: null,
          error: null
        },
        async () => {
          try {
            const result = await this.props.load({
              url: this.props.url,
              method: this.props.method,
              headers: Credentials.getHeaders(this.props.customUrl),
              cancelToken: this.state.source ? this.state.source.token : '',
              onUploadProgress: ({ loaded, total }: { loaded: number, total: number }) => {
                if (loaded && total) {
                  this.setState({ progress: loaded / total });
                }
              },
              ...this.props.options
            });

            this.setState({ pending: false, progress: 0, result }, () => {
                if (this.props.onLoaded) {
                  this.props.onLoaded(null, result);
                }
              }
            );
          } catch (error) {
            console.warn(error);

            if (!axios.isCancel(error)) {
              this.setState({ pending: false, progress: 0 }, () => {
                  if (this.props.onLoaded) {
                    this.props.onLoaded(error);
                  }
                }
              );
            }

            this.setState({ error });

            if (this.props.onLoaded) {
              this.props.onLoaded(error);
            }
          }
        }
      );
    }
  };

  /**
   * Aborting started request.
   */
  handleAbort = () => {
    if (!_.isNull(this.state.source)) {
      if (this.state.source) {
        this.state.source.cancel();
      }
      this.setState({
        pending: false,
        source: null,
        progress: 0,
        result: null,
        canceled: true,
        error: null
      });
    }
  };

  render = (): React.Node => {
    if (this.props.disabled) {
      return this.props.children;
    }

    if (this.props.useNProgress) {
      NProgressCustom(!this.state.pending);
    }

    if (this.props.showChildrenAlways) {
      return this.props.children;
    }

    const { Pending } = this.props;

    if (this.state.pending) {
      return (
        <Pending onClick={this.handleAbort} progress={this.state.progress}>
          <span className='Loader__pending-msg'>{this.props.loadMessage}</span>
        </Pending>
      );
    }

    const { Canceled } = this.props;

    if (this.state.canceled) {
      return (
        <Canceled onClick={this.handleLoad}>
          {this.props.cancelMessage}
        </Canceled>
      );
    }

    const { Error } = this.props;

    if (this.state.error) {
      return (
        <Error onClick={this.handleError}>
          {this.props.errorMessage}
        </Error>
      );
    }

    const { Message } = this.props;

    if (this.props.emptyDataCondition(this.state.result)) {
      return (
        <Message onClick={this.handleLoad}>
          {this.props.emptyDataMessage}
        </Message>
      );
    }

    return this.props.children ? this.props.children : null;
  };
}

export default Loader;
